/**
 * Lets any admin surface ask the app-level OrgOnboardingGate to open the setup
 * wizard.
 *
 * The wizard is mounted once in App.tsx and was previously reachable only by
 * the gate's own auto-open, so an admin who dismissed it — or who finished it
 * once — had no way back in. This is deliberately a module-level emitter rather
 * than a context provider: exactly one listener (the gate) and no state to
 * share.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

export const requestOrgSetupRelaunch = (): void => {
  listeners.forEach((fn) => fn());
};

export const onOrgSetupRelaunch = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/**
 * The gate announces the wizard opening and closing, so a surface that writes
 * the same organization row can yield while the wizard owns it.
 *
 * The profile editor is the surface that needs this. It is mounted UNDER the
 * wizard on the admin home, holds the whole profile document in memory, and
 * autosaves that whole document — so a wizard write it did not know about was
 * silently overwritten by the admin's next keystroke (eHomes, 2026-09-10). On
 * open it flushes what it holds and goes quiet; on close it re-hydrates from
 * the row.
 */
type OpenListener = (open: boolean) => void;

const openListeners = new Set<OpenListener>();
let wizardOpen = false;

/** Whether the wizard is open right now, for a subscriber mounting mid-session. */
export const isOrgWizardOpen = (): boolean => wizardOpen;

export const announceOrgWizardOpen = (open: boolean): void => {
  if (open === wizardOpen) return;
  wizardOpen = open;
  openListeners.forEach((fn) => fn(open));
};

export const onOrgWizardOpenChange = (fn: OpenListener): (() => void) => {
  openListeners.add(fn);
  return () => {
    openListeners.delete(fn);
  };
};
